import { Animatable } from '@babylonjs/core/Animations/animatable';
import { Animation } from '@babylonjs/core/Animations/animation';
import { CircleEase, SineEase } from '@babylonjs/core/Animations/easing';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Camera } from '@babylonjs/core/Cameras/camera';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { BoundingSphere } from '@babylonjs/core/Culling/boundingSphere';
import { Plane, Vector2, Vector3 } from '@babylonjs/core/Maths';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { PostProcessRenderPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderPipeline';
import { WebXRCamera } from '@babylonjs/core/XR/webXRCamera';
import { Scene } from '@babylonjs/core/scene';
import { Nullable } from '@babylonjs/core/types';
import { AnnotationNode, DimensionNode } from '@models/classes/annotations';
import { defaults } from '@models/classes/default-values';
import { eViewpointProjection, eViewpointTargetMode, eViewpointType } from '@models/classes/enums';
import { MeshNode, SpaceNode } from '@models/classes/meshes';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { ViewpointNode } from '@models/classes/viewpoints/viewpoint-node';
import { getCameraPositionWithOffset, toRadians } from '@view/helpers/math-helper';
import { AnnotationInfo, SKYBOX_SIZE } from '@view/render';
import { Observable, Subject } from 'rxjs';
import { finalize, share, startWith, takeUntil } from 'rxjs/operators';
import { getScreenBoundingBox, nodeToScreen } from '../helpers/scene-helper';
import { KbViewer } from '../kb-viewer';
import { KbArcRotateCamera } from './cameras/kb-arc-rotate-camera';

const ANIMATION_FPS = 30;

enum eCameraType {
    Universal,
    ArcRotate,
}

export interface ICameraAnimationOptions {
    duration: number;
    frame?: boolean;
    frameOptions?: Partial<IFramingOptions>;
    frameTarget?: SpaceNode;
}

export interface IFramingOptions {
    rotate: boolean;
    pan: boolean;
    zoom: boolean;
    padding: number;
}

export enum eCameraOrientation {
    perspective = 'perspective',
    front = 'front',
    left = 'left',
    back = 'back',
    right = 'right',
    top = 'top',
    bottom = 'bottom',
}

type ScalingHandler = (distance: number, type: number) => void;

export class CameraTools {
    get cameraMoving() {
        const camera = this.scene.activeCamera;
        if (this._activeAnimation) {
            return true;
        }
        if (camera instanceof KbArcRotateCamera) {
            return (
                Math.abs(camera.inertialRadiusOffset) > 0 ||
                Math.abs(camera.inertialAlphaOffset) > 0 ||
                Math.abs(camera.inertialBetaOffset) > 0 ||
                Math.abs(camera.inertialOffsetPanningX) > 0 ||
                Math.abs(camera.inertialOffsetPanningY) > 0 ||
                Math.abs(camera.inertialOrthoZoom) > 0
            );
        }
        if (camera instanceof UniversalCamera) {
            return (
                Math.abs(camera.cameraDirection.x) > 0 ||
                Math.abs(camera.cameraDirection.y) > 0 ||
                Math.abs(camera.cameraDirection.z) > 0 ||
                Math.abs(camera.cameraRotation.x) > 0 ||
                Math.abs(camera.cameraRotation.y) > 0
            );
        }
        return false;
    }

    public activeCamera: Partial<ViewpointNode>;

    private _cameraSettingsChange?: Subject<Partial<ViewpointNode>>;
    private _beforeRender: () => void;
    private _storedViewpoint?: ViewpointNode;
    private _activeAnimation?: Animatable;
    private disposePreviousCamera: Subject<void> | undefined;
    private _lastOrientation = eCameraOrientation.perspective;
    private _lastPerspectivePosition: KbVector;
    private minVector = new Vector3(0, 0, 0);
    private maxVector = new Vector3(0, 0, 0);
    private activePipeline: PostProcessRenderPipeline | undefined;

    private objectsToScaleWithDistance = new Map<TransformNode, number | ScalingHandler>();

    constructor(private scene: Scene, private canvas: HTMLElement, private kbViewer: KbViewer) {
        this._beforeRender = () => {
            if (this._cameraSettingsChange && scene.activeCamera && this.cameraMoving) {
                const camera = scene.activeCamera as KbArcRotateCamera | UniversalCamera;
                const changeList: Partial<ViewpointNode> = {};
                copyCameraStateIntoViewpoint(changeList, camera);

                this._cameraSettingsChange.next(changeList);
            }
        };
        scene.registerBeforeRender(this._beforeRender);

        kbViewer.viewChanged.subscribe(() => {
            const camera = scene.activeCamera;
            if (camera) {
                if (camera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                    const dist = ((camera.orthoRight || 0) - (camera.orthoLeft || 0)) * 0.6;
                    this.objectsToScaleWithDistance.forEach((param, item) => {
                        if (item.isDisposed()) {
                            this.objectsToScaleWithDistance.delete(item);
                            return;
                        }
                        if (typeof param === 'function') {
                            param(dist, camera.mode);
                        } else {
                            const scaledSize = param * dist;
                            item.scaling.set(scaledSize, scaledSize, scaledSize);
                        }
                    });
                } else {
                    let cameraPosition: Vector3;
                    if (camera instanceof WebXRCamera && camera.globalPosition) {
                        cameraPosition = camera.globalPosition;
                    } else if (camera instanceof ArcRotateCamera) {
                        cameraPosition = getCameraPositionWithOffset(camera);
                    } else {
                        cameraPosition = camera.globalPosition;
                    }
                    this.objectsToScaleWithDistance.forEach((param, item) => {
                        if (item.isDisposed()) {
                            this.objectsToScaleWithDistance.delete(item);
                            return;
                        }
                        const dist = item.absolutePosition.subtract(cameraPosition).length();
                        if (typeof param === 'function') {
                            param(dist, camera.mode);
                        } else {
                            const scaledSize = dist * param;
                            item.scaling.set(scaledSize, scaledSize, scaledSize);
                        }
                    });
                }
            }
        });

        this.activeCamera = {
            get position() {
                return scene.activeCamera ? KbVector.FromVec3(scene.activeCamera.position) : KbVector.Zero();
            },
            set position(vector: KbVector) {
                if (scene.activeCamera) {
                    if (
                        scene.activeCamera.mode === Camera.ORTHOGRAPHIC_CAMERA &&
                        scene.activeCamera instanceof ArcRotateCamera
                    ) {
                        const target = scene.activeCamera.target;
                        scene.activeCamera.position = vector
                            .toVec3()
                            .subtract(target)
                            .normalize()
                            .scale(5000)
                            .add(target);
                    } else {
                        scene.activeCamera.position = vector.toVec3();
                    }
                }
            },
            get targetPoint() {
                return scene.activeCamera instanceof ArcRotateCamera
                    ? KbVector.FromVec3(scene.activeCamera.target)
                    : KbVector.Zero();
            },
            set targetPoint(vector: KbVector) {
                scene.activeCamera instanceof ArcRotateCamera && (scene.activeCamera.target = vector.toVec3());
            },
            get rotation() {
                return scene.activeCamera instanceof UniversalCamera
                    ? KbVector.FromVec3(scene.activeCamera.rotation)
                    : KbVector.Zero();
            },
            set rotation(vector: KbVector) {
                scene.activeCamera instanceof UniversalCamera && (scene.activeCamera.rotation = vector.toVec3());
            },
            get offsetX() {
                return scene.activeCamera instanceof ArcRotateCamera ? scene.activeCamera.targetScreenOffset!.x : 0;
            },
            set offsetX(offset: number) {
                scene.activeCamera instanceof ArcRotateCamera && (scene.activeCamera.targetScreenOffset.x = offset);
            },
            get offsetY() {
                return scene.activeCamera instanceof ArcRotateCamera ? scene.activeCamera.targetScreenOffset!.y : 0;
            },
            set offsetY(offset: number) {
                scene.activeCamera instanceof ArcRotateCamera && (scene.activeCamera.targetScreenOffset.y = offset);
            },
            get projection() {
                if (!scene.activeCamera) {
                    return undefined;
                }
                return scene.activeCamera.mode === Camera.PERSPECTIVE_CAMERA
                    ? eViewpointProjection.perspective
                    : eViewpointProjection.orthographic;
            },
            set projection(projection: eViewpointProjection | undefined) {
                scene.activeCamera &&
                    (scene.activeCamera.mode =
                        projection === eViewpointProjection.perspective
                            ? Camera.PERSPECTIVE_CAMERA
                            : Camera.ORTHOGRAPHIC_CAMERA);
            },
            get fov() {
                if (!scene.activeCamera) {
                    return 50;
                }
                return scene.activeCamera.mode === Camera.ORTHOGRAPHIC_CAMERA
                    ? scene.activeCamera.orthoRight! - scene.activeCamera.orthoLeft!
                    : (scene.activeCamera.fov * 180) / Math.PI;
            },
            set fov(fov: number) {
                if (!scene.activeCamera) {
                    return;
                }
                if (
                    scene.activeCamera.mode === Camera.ORTHOGRAPHIC_CAMERA &&
                    scene.activeCamera instanceof KbArcRotateCamera
                ) {
                    scene.activeCamera.orthoWidth = fov;
                } else {
                    scene.activeCamera.fov = (fov * Math.PI) / 180;
                }
            },
            get allowOrbit() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return !scene.activeCamera.lockRotation;
                } else if (scene.activeCamera instanceof UniversalCamera) {
                    return !!scene.activeCamera.inputs.attached.mouse;
                }
                return false;
            },
            set allowOrbit(allow) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.lockRotation = !allow;
                } else if (scene.activeCamera instanceof UniversalCamera) {
                    scene.activeCamera.inputs.clear();
                    enableFreeLook(scene.activeCamera);
                }
            },
            get allowPan() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return !scene.activeCamera.lockPanning;
                } else if (scene.activeCamera instanceof UniversalCamera) {
                    return !!scene.activeCamera.inputs.attached.keyboard;
                }
                return false;
            },
            set allowPan(allow) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.lockPanning = !allow;
                } else if (scene.activeCamera instanceof UniversalCamera) {
                    scene.activeCamera.inputs.clear();
                    enableFreeMove(scene.activeCamera);
                }
            },
            get allowZoom() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return !scene.activeCamera.lockZoom;
                }
                return false;
            },
            set allowZoom(allow) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.lockZoom = !allow;
                }
            },
            get limitMinimumTargetDistance() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return scene.activeCamera.limitMinimumTargetDistance;
                }
                return false;
            },
            set limitMinimumTargetDistance(allow) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.limitMinimumTargetDistance = !allow;
                }
            },
            get minTargetDistance() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return scene.activeCamera.lowerRadiusLimit || defaults.ViewpointNode.minTargetDistance;
                }
                return defaults.ViewpointNode.minTargetDistance;
            },
            set minTargetDistance(value) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.lowerRadiusLimit = value;
                }
            },
            get maxTargetDistance() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return scene.activeCamera.upperRadiusLimit || defaults.ViewpointNode.maxTargetDistance;
                }
                return defaults.ViewpointNode.maxTargetDistance;
            },
            set maxTargetDistance(value) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.upperRadiusLimit = value;
                }
            },

            get orbitAxis() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return KbVector.FromVec3(scene.activeCamera.upVector);
                }
                return KbVector.From(defaults.ViewpointNode.orbitAxis);
            },
            set orbitAxis(value) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.upVector = value.toVec3();
                }
            },

            get limitMinLatitude() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return ((scene.activeCamera.lowerBetaLimit || 0) * 180) / Math.PI;
                }
                return defaults.ViewpointNode.limitMinLatitude;
            },
            set limitMinLatitude(value) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.lowerBetaLimit = (value * Math.PI) / 180;
                }
            },
            get limitMaxLatitude() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return (
                        ((typeof scene.activeCamera.upperBetaLimit === 'number'
                            ? scene.activeCamera.upperBetaLimit
                            : Math.PI) *
                            180) /
                        Math.PI
                    );
                }
                return defaults.ViewpointNode.limitMaxLatitude;
            },
            set limitMaxLatitude(value) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.upperBetaLimit = (value * Math.PI) / 180;
                }
            },
            get limitMinLongitude() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return scene.activeCamera.lowerAlphaLimit
                        ? (scene.activeCamera.lowerAlphaLimit * 180) / Math.PI
                        : defaults.ViewpointNode.limitMinLongitude;
                }
                return defaults.ViewpointNode.limitMinLongitude;
            },
            set limitMinLongitude(value) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.lowerAlphaLimit = (value * Math.PI) / 180;
                }
            },
            get limitMaxLongitude() {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    return scene.activeCamera.upperAlphaLimit
                        ? (scene.activeCamera.upperAlphaLimit * 180) / Math.PI
                        : defaults.ViewpointNode.limitMinLongitude;
                }
                return defaults.ViewpointNode.limitMinLongitude;
            },
            set limitMaxLongitude(value) {
                if (scene.activeCamera instanceof KbArcRotateCamera) {
                    scene.activeCamera.upperAlphaLimit = (value * Math.PI) / 180;
                }
            },
        };
    }

    dispose() {
        this.scene.unregisterBeforeRender(this._beforeRender);
    }

    setPipeline(pipeline: PostProcessRenderPipeline) {
        this.activePipeline = pipeline;
        if (this.scene.activeCamera) {
            this.applyScenePostProcesses(this.scene.activeCamera);
        }
    }

    /** Frames the current active camera to fit the provided mesh node
     * @param node The node to fit on the screen
     * @param padding If provided, pads the node by a percentage of the node's size.
     */
    frameCamera(node: SpaceNode, options?: Partial<IFramingOptions>, camera?: Camera) {
        const _options: IFramingOptions = {
            rotate: false,
            pan: true,
            padding: 1,
            zoom: true,
            ...(options || {}),
        };
        const activeCamera = camera || this.scene.activeCamera;
        if (!activeCamera) {
            throw Error('No active camera');
        }

        const bounding = this.kbViewer.bbCache.getWorld(node);

        if (!bounding || bounding.boundingInfo.diagonalLength === 0) {
            return; // no op if the bounding box doesn't exist
        } else {
            const childrenBounding = this.kbViewer.sceneNode.getChildren();
            const maxVector = bounding.boundingInfo.boundingBox.maximum;
            const minVector = bounding.boundingInfo.boundingBox.minimum;
            childrenBounding.forEach(element => {
                if (
                    (element instanceof AnnotationNode && element.enabled) ||
                    (element instanceof DimensionNode && element.enabled)
                ) {
                    let nodeInfo: AnnotationInfo | undefined;
                    if (element instanceof AnnotationNode) {
                        nodeInfo = this.kbViewer.rendererManager.annotation.annotationStore.get(element._dynamicId);
                    }
                    if (element instanceof DimensionNode) {
                        nodeInfo = this.kbViewer.rendererManager.dimension.dimensionStore.get(element._dynamicId);
                    }
                    const minBoundingBox = nodeInfo?.meshInfo?.textContainer.getHierarchyBoundingVectors().min;
                    const maxBoundingBox = nodeInfo?.meshInfo?.textContainer.getHierarchyBoundingVectors().max;
                    if (minBoundingBox && minBoundingBox.x < minVector.x) {
                        minVector.x = minBoundingBox.x;
                    }
                    if (minBoundingBox && minBoundingBox.y < minVector.y) {
                        minVector.y = minBoundingBox.y;
                    }
                    if (minBoundingBox && minBoundingBox.z < minVector.z) {
                        minVector.z = minBoundingBox.z;
                    }
                    if (maxBoundingBox && maxBoundingBox.x > maxVector.x) {
                        maxVector.x = maxBoundingBox.x;
                    }
                    if (maxBoundingBox && maxBoundingBox.y > maxVector.y) {
                        maxVector.y = maxBoundingBox.y;
                    }
                    if (maxBoundingBox && maxBoundingBox.z > maxVector.z) {
                        maxVector.z = maxBoundingBox.z;
                    }
                }
            });
            this.minVector.set(minVector.x, minVector.y, minVector.z);

            this.maxVector.set(maxVector.x, maxVector.y, maxVector.z);

            bounding.boundingInfo.boundingBox.reConstruct(
                this.minVector,
                this.maxVector,
                bounding.boundingInfo.boundingBox.getWorldMatrix()
            );
            const bbCenter = bounding.center.toVec3();

            const bbCorners = bounding.boundingInfo.boundingBox.vectorsWorld;

            if (activeCamera instanceof UniversalCamera) {
                activeCamera.setTarget(bbCenter);
            } else if (activeCamera instanceof KbArcRotateCamera) {
                if (_options.rotate) {
                    activeCamera.setTarget(bbCenter);
                    if (_options.pan) {
                        activeCamera.targetScreenOffset.set(0, 0);
                    }
                } else if (_options.pan) {
                    const cameraRay = activeCamera.getForwardRay();
                    const cameraProjectionTargetPlane = Plane.FromPositionAndNormal(bbCenter, cameraRay.direction);
                    const cameraDistance = cameraRay.intersectsPlane(cameraProjectionTargetPlane);
                    const cameraProjectionTargetPoint = cameraRay.origin.add(
                        cameraRay.direction.scale(cameraDistance!)
                    );
                    const cameraPositionWorldOffset = cameraProjectionTargetPoint.subtract(bbCenter);

                    const planeProjectionX = Vector3.Cross(cameraRay.direction, Vector3.Up()).normalize();
                    const planeProjectionY = Vector3.Cross(cameraRay.direction, planeProjectionX).normalize();

                    const bbCenterOffset = new Vector2(
                        Vector3.Dot(cameraPositionWorldOffset, planeProjectionX),
                        -Vector3.Dot(cameraPositionWorldOffset, planeProjectionY)
                    );
                    activeCamera.targetScreenOffset.copyFrom(bbCenterOffset);
                }

                if (_options.zoom) {
                    const clientRect = this.canvas.getBoundingClientRect();
                    const ratio = clientRect.height / clientRect.width;
                    const cameraRay = activeCamera.getForwardRay();
                    const xAxis = Vector3.Cross(cameraRay.direction, Vector3.Up()).normalize();
                    const yAxis = Vector3.Cross(cameraRay.direction, xAxis).normalize();
                    const cameraTruePosition = cameraRay.origin
                        .add(xAxis.scale(-activeCamera.targetScreenOffset.x))
                        .add(yAxis.scale(activeCamera.targetScreenOffset.y));
                    const cameraTrueTarget = activeCamera.target
                        .add(xAxis.scale(-activeCamera.targetScreenOffset.x))
                        .add(yAxis.scale(activeCamera.targetScreenOffset.y));
                    const xPlane = Plane.FromPositionAndNormal(cameraTruePosition, yAxis);
                    const yPlane = Plane.FromPositionAndNormal(cameraTruePosition, xAxis);
                    const padding = 1 + _options.padding / 100;

                    if (activeCamera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                        let maxX = 0,
                            maxY = 0;
                        bbCorners.forEach(bbCorner => {
                            const xO = xPlane.signedDistanceTo(bbCorner) * padding;
                            maxX = Math.max(maxX, Math.abs(xO));

                            const yO = yPlane.signedDistanceTo(bbCorner) * padding;
                            maxY = Math.max(maxY, Math.abs(yO));
                        });

                        // The calculated distance is the maximum distance from the centerline to the bounding box corner. So double it to get the total width/height
                        activeCamera.orthoFit(maxY * 2, maxX * 2);
                    } else {
                        let desiredDistance = 0;
                        const fovH = 2 * Math.atan(Math.tan(activeCamera.fov / 2) / ratio);
                        const thV = Math.tan(activeCamera.fov / 2);
                        const thH = Math.tan(fovH / 2);
                        bbCorners.forEach(bbCorner => {
                            const xO = xPlane.signedDistanceTo(bbCorner) * padding;
                            const xP = bbCorner.subtract(cameraTrueTarget).subtract(yAxis.scale(xO));
                            const xA = Math.abs(xO) / thV;
                            const xR = Vector3.Dot(xP, cameraRay.direction.scale(-1)) + xA;
                            desiredDistance = Math.max(desiredDistance, xR);

                            const yO = yPlane.signedDistanceTo(bbCorner) * padding;
                            const yP = bbCorner.subtract(cameraTrueTarget).subtract(xAxis.scale(yO));
                            const yA = Math.abs(yO) / thH;
                            const yR = Vector3.Dot(yP, cameraRay.direction.scale(-1)) + yA;
                            desiredDistance = Math.max(desiredDistance, yR);
                        });

                        activeCamera.radius = Math.max(desiredDistance, 0.001);
                    }
                }
            }
            this.kbViewer.fireViewChangedOnNextRender(); //render loop won't pick up that the view changed, because no kb3d nodes changes, so we must update it explicitly
        }
    }

    getScreenPositionOfNode(node: MeshNode) {
        if (!this.scene.activeCamera) {
            throw Error('No active camera');
        }
        const bNode = this.kbViewer.getRendererNode(node);

        if (bNode) {
            return nodeToScreen(bNode, this.scene.activeCamera);
        }
    }

    getScreenBoundingBoxOfNode(node: MeshNode) {
        if (!this.scene.activeCamera) {
            throw Error('No active camera');
        }
        const bNode = this.kbViewer.getRendererNode(node);
        if (bNode) {
            return getScreenBoundingBox(bNode, this.scene.activeCamera, true);
        }
    }

    moveCameraToViewpoint(viewpoint: ViewpointNode) {
        const newCamera = this.createCameraFromViewpoint(viewpoint)!;
        this._activateCamera(newCamera);
    }

    animateToViewpoint(viewpoint: ViewpointNode, options?: Partial<ICameraAnimationOptions>) {
        if (!this.scene.activeCamera) {
            throw Error('No active camera');
        }
        let activeCamera = this.scene.activeCamera;
        const _options: ICameraAnimationOptions = {
            duration: 1000,
            ...(options || {}),
        };
        const endFrame = 30 * (_options.duration / 1000);
        const animations: Animation[] = [];
        const ease = new SineEase();
        ease.setEasingMode(SineEase.EASINGMODE_EASEINOUT);
        const circEaseOut = new CircleEase();
        circEaseOut.setEasingMode(CircleEase.EASINGMODE_EASEOUT);

        // Create the new viewpoint converted to an arc rotate camera so we can tween arc rotate properties
        // The arc rotate cameras are easier to animate between nicely.
        const newCameraTarget = this.createCameraFromViewpoint(viewpoint, eCameraType.ArcRotate)!;
        const newCamera = this.createCameraFromViewpoint(viewpoint)!;

        if (_options.frame && newCamera instanceof ArcRotateCamera) {
            const target = _options.frameTarget || viewpoint._parentScene;
            if (!target) {
                throw Error('Cannot frame camera, no target node to frame');
            } else {
                this.frameCamera(target, _options.frameOptions || {}, newCameraTarget);
                this.frameCamera(target, _options.frameOptions || {}, newCamera);
            }
        }
        if (activeCamera instanceof ArcRotateCamera) {
            if (Math.abs(activeCamera.alpha) > Math.PI * 2) {
                activeCamera.alpha = activeCamera.alpha % (Math.PI * 2);
            }
        }
        if (activeCamera instanceof UniversalCamera && viewpoint.type != eViewpointType.firstPerson) {
            const orbitRotation = activeCamera.rotationQuaternion
                ? activeCamera.rotationQuaternion.toEulerAngles()
                : activeCamera.rotation;
            const ray = activeCamera.getForwardRay(newCameraTarget.radius);
            // If the current camera is a universal camera, convert it to an arc rotate camera as well/
            // The arc rotate target position is placed from the camera at the same distance as the destination viewpoint's target
            activeCamera = new KbArcRotateCamera(
                'temporaryCamera',
                -(orbitRotation.y + Math.PI / 2),
                Math.PI / 2 - orbitRotation.x,
                ray.length,
                ray.direction.scale(ray.length).add(ray.origin),
                this.scene
            );
            activeCamera.fov = this.scene.activeCamera.fov;
            this._activateCamera(activeCamera);
        }

        if (!newCameraTarget) {
            throw Error('Could not create new camera');
        }

        if (activeCamera instanceof UniversalCamera && viewpoint.type === eViewpointType.firstPerson) {
            const targetRotation = new Vector3(0, 0, 0);
            const targetPosition = new Vector3(viewpoint.position.x, viewpoint.position.y, viewpoint.position.z);
            targetRotation.y = (viewpoint.rotation.y * Math.PI) / 180;
            targetRotation.x = (viewpoint.rotation.x * Math.PI) / 180;
            targetRotation.z = (viewpoint.rotation.z * Math.PI) / 180;

            let activeAlpha = activeCamera.rotation.y;
            let targetAlpha = (viewpoint.rotation.y * Math.PI) / 180;
            let arc = 0;

            //Normalizing values to be between -PI and PI
            activeAlpha = normalizeRotation(activeAlpha);
            targetAlpha = normalizeRotation(targetAlpha);

            //calculating shortest arc and seeing if it crosses zero
            //if it does, change the target vector to account for expected direction of animation
            if (activeAlpha > targetAlpha) {
                arc = activeAlpha - targetAlpha;
            } else {
                arc = targetAlpha - activeAlpha;
            }
            if (arc > Math.PI) {
                if (activeAlpha < targetAlpha) {
                    targetRotation.y = targetRotation.y - 2 * Math.PI;
                } else {
                    targetRotation.y = targetRotation.y + 2 * Math.PI;
                }
            }

            const translateRotation = new Animation(
                'translateCameraRotation',
                'rotation',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_VECTOR3,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );
            translateRotation.setKeys([
                { frame: 0, value: activeCamera.rotation },
                { frame: endFrame, value: targetRotation },
            ]);

            const translatePosition = new Animation(
                'translateCameraPosition',
                'position',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_VECTOR3,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );
            translatePosition.setKeys([
                { frame: 0, value: activeCamera.position },
                { frame: endFrame, value: targetPosition },
            ]);
            const translateFov = new Animation(
                'translateCameraFOV',
                'fov',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_FLOAT,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );
            translateFov.setKeys([
                { frame: 0, value: activeCamera.fov },
                { frame: endFrame, value: (viewpoint.fov / 180) * Math.PI },
            ]);
            translateRotation.setEasingFunction(ease);
            translatePosition.setEasingFunction(ease);
            translateFov.setEasingFunction(ease);

            animations.push(translateRotation);
            animations.push(translatePosition);
            animations.push(translateFov);
        } else if (newCameraTarget instanceof ArcRotateCamera && activeCamera instanceof ArcRotateCamera) {
            // When rotating between two arc rotate cameras, rotate around the target while animating the target

            if (!activeCamera.target.equals(newCameraTarget.target)) {
                const translateTarget = new Animation(
                    'translateCameraTarget',
                    'target',
                    ANIMATION_FPS,
                    Animation.ANIMATIONTYPE_VECTOR3,
                    Animation.ANIMATIONLOOPMODE_CONSTANT
                );
                translateTarget.setKeys([
                    { frame: 0, value: activeCamera.target },
                    { frame: endFrame, value: newCameraTarget.target },
                ]);
                animations.push(translateTarget);
            }

            const translateA = new Animation(
                'translateCameraA',
                'alpha',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_FLOAT,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );

            let crossesZero = false;
            let arc = 0;
            let shortestArc = 0;

            //Normalizing values to be between -PI and PI
            activeCamera.alpha = normalizeRotation(activeCamera.alpha);
            newCameraTarget.alpha = normalizeRotation(newCameraTarget.alpha);

            //calculating shortest arc between two cameras
            if (activeCamera.alpha > newCameraTarget.alpha) {
                arc = activeCamera.alpha - newCameraTarget.alpha;
            } else {
                arc = newCameraTarget.alpha - activeCamera.alpha;
            }
            if (arc > Math.PI) {
                shortestArc = 2 * Math.PI - arc;
                crossesZero = true;
            }

            //altering newCameraTarget to take shortest path
            if (newCameraTarget.alpha > 0 && crossesZero) {
                newCameraTarget.alpha = activeCamera.alpha - shortestArc;
            } else if (newCameraTarget.alpha < 0 && crossesZero) {
                newCameraTarget.alpha = activeCamera.alpha + shortestArc;
            }

            translateA.setKeys([
                { frame: 0, value: activeCamera.alpha },
                { frame: endFrame, value: newCameraTarget.alpha },
            ]);
            const translateB = new Animation(
                'translateCameraB',
                'beta',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_FLOAT,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );
            translateB.setKeys([
                { frame: 0, value: activeCamera.beta },
                { frame: endFrame, value: newCameraTarget.beta },
            ]);
            const translateR = new Animation(
                'translateCameraR',
                'radius',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_FLOAT,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );
            translateR.setKeys([
                { frame: 0, value: activeCamera.radius },
                { frame: endFrame, value: newCameraTarget.radius },
            ]);
            const translateOffset = new Animation(
                'translateCameraOffset',
                'targetScreenOffset',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_VECTOR2,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );
            translateOffset.setKeys([
                { frame: 0, value: activeCamera.targetScreenOffset },
                { frame: endFrame, value: newCameraTarget.targetScreenOffset },
            ]);
            const translateFov = new Animation(
                'translateCameraFOV',
                'fov',
                ANIMATION_FPS,
                Animation.ANIMATIONTYPE_FLOAT,
                Animation.ANIMATIONLOOPMODE_CONSTANT
            );
            translateFov.setKeys([
                { frame: 0, value: activeCamera.fov },
                { frame: endFrame, value: newCameraTarget.fov },
            ]);
            if (
                activeCamera instanceof KbArcRotateCamera &&
                activeCamera.mode === Camera.ORTHOGRAPHIC_CAMERA &&
                newCameraTarget.mode === Camera.ORTHOGRAPHIC_CAMERA
            ) {
                const translateOrthoWidth = new Animation(
                    'translateOrthoWidth',
                    'orthoWidth',
                    ANIMATION_FPS,
                    Animation.ANIMATIONTYPE_FLOAT,
                    Animation.ANIMATIONLOOPMODE_CONSTANT
                );
                translateOrthoWidth.setKeys([
                    { frame: 0, value: activeCamera.orthoWidth },
                    { frame: endFrame, value: newCameraTarget.orthoWidth },
                ]);
                translateOrthoWidth.setEasingFunction(ease);

                animations.push(translateOrthoWidth);
            }

            translateA.setEasingFunction(ease);
            translateB.setEasingFunction(ease);
            translateR.setEasingFunction(ease);
            translateOffset.setEasingFunction(ease);
            translateFov.setEasingFunction(circEaseOut);

            animations.push(translateA);
            animations.push(translateB);
            animations.push(translateR);
            animations.push(translateOffset);
            animations.push(translateFov);
        }

        animations.forEach(animation => {
            activeCamera.animations.push(animation);
        });

        return new Promise<void>(resolve => {
            this._activeAnimation = this.scene.beginAnimation(activeCamera, 0, endFrame, false, 1, () => {
                // on end of animation
                delete this._activeAnimation;

                this._activateCamera(newCamera);
                resolve();
            });
        });
    }

    storeCameraSettings() {
        if (!this.scene.activeCamera) {
            throw Error('No active camera');
        }
        this._storedViewpoint = new ViewpointNode();
        const camera = this.scene.activeCamera as KbArcRotateCamera | UniversalCamera;

        copyCameraStateIntoViewpoint(this._storedViewpoint, camera);
    }

    restoreCameraSettings() {
        if (this._storedViewpoint) {
            this.moveCameraToViewpoint(this._storedViewpoint);
        } else {
            console.warn('No stored camera settings to restore');
        }
    }

    watch(): Observable<Partial<ViewpointNode>> {
        this._cameraSettingsChange = new Subject();

        return this._cameraSettingsChange.pipe(
            finalize(() => {
                delete this._cameraSettingsChange;
            }),
            share()
        );
    }

    maintainNodeScreenSize(renderedNode: TransformNode, relativeSize: number | ScalingHandler) {
        this.objectsToScaleWithDistance.set(renderedNode, relativeSize);
    }

    clearMaintainedNodeScreenSize(renderedNode: TransformNode) {
        this.objectsToScaleWithDistance.delete(renderedNode);
    }

    private _activateCamera(newCamera: Camera) {
        this.scene.cameras.forEach(camera => {
            if (camera !== newCamera) {
                /**
                 * Don't dispose the whole camera because that goes too hard and disposes all the render target textures and
                 * post processes which messes things up for the next camera that's reusing those things. Instead, just dispose the
                 * parts of the camera we don't need.
                 */
                camera.onViewMatrixChangedObservable.clear();
                camera.onProjectionMatrixChangedObservable.clear();
                camera.onAfterCheckInputsObservable.clear();
                camera.onRestoreStateObservable.clear();

                // Inputs
                if (camera.inputs) {
                    camera.inputs.clear();
                }

                // Animations
                this.scene.stopAnimation(camera);

                // Remove from scene
                this.scene.removeCamera(camera);

                this.scene.getEngine().releaseRenderPassId(camera.renderPassId);
            }
        });
        this.scene.customRenderTargets.forEach(rtt => {
            rtt.activeCamera = newCamera;
        });
        this.applyScenePostProcesses(newCamera);
        newCamera!.attachControl(false);
        this.scene.activeCamera = newCamera;
    }

    private applyScenePostProcesses(camera: Camera) {
        if (this.activePipeline) {
            this.scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline(this.activePipeline.name, camera);
        }
    }

    private createCameraFromViewpoint(
        viewpoint: ViewpointNode,
        convertToCameraType: eCameraType.Universal
    ): Nullable<UniversalCamera>;
    private createCameraFromViewpoint(
        viewpoint: ViewpointNode,
        convertToCameraType: eCameraType.ArcRotate
    ): Nullable<KbArcRotateCamera>;
    private createCameraFromViewpoint(viewpoint: ViewpointNode, convertToCameraType?: eCameraType): Nullable<Camera>;
    private createCameraFromViewpoint(viewpoint: ViewpointNode, convertToCameraType?: eCameraType): Nullable<Camera> {
        const viewer = this.kbViewer;
        const bbCache = this.kbViewer.bbCache;
        const sceneNode = this.kbViewer.sceneNode;

        if (this.disposePreviousCamera && !this.disposePreviousCamera.isStopped) {
            this.disposePreviousCamera.next();
            this.disposePreviousCamera.complete();
        }
        const dispose = (this.disposePreviousCamera = new Subject());

        switch (viewpoint.type) {
            case eViewpointType.orbit:
            case eViewpointType.overhead:
                if (convertToCameraType === eCameraType.Universal) {
                    const universalCamera = new UniversalCamera(viewpoint.id, viewpoint.position.toVec3(), this.scene);

                    // this should be set in the setCommonViewpointProperties
                    // const direction = viewpoint.targetPoint!.subtract(viewpoint.position).toVec3();
                    // universalCamera.rotation = rotationToVector(direction).toEulerAngles();
                    applyViewpointProperties(viewpoint, universalCamera);

                    return universalCamera;
                } else {
                    const arcRotateCamera = new KbArcRotateCamera(
                        viewpoint.id,
                        0,
                        0,
                        0,
                        viewpoint.targetPoint ? viewpoint.targetPoint.toVec3() : Vector3.Zero(),
                        this.scene
                    );

                    applyViewpointProperties(viewpoint, arcRotateCamera);

                    // overhead camera overrides target
                    if (viewpoint.type === eViewpointType.overhead) {
                        arcRotateCamera.lockRotation = true;
                        arcRotateCamera.target = new Vector3(viewpoint.position.x, 0, viewpoint.position.z);
                    }

                    return arcRotateCamera;
                }
            case eViewpointType.firstPerson:
                //case eViewpointType.fixedPosition:
                if (convertToCameraType === eCameraType.ArcRotate) {
                    const arcRotateCamera = new KbArcRotateCamera(
                        viewpoint.id,
                        0,
                        0,
                        0,
                        viewpoint.position.toVec3(),
                        this.scene
                    );

                    const orbitRotation = viewpoint.rotation.toVec3().scale(Math.PI / 180);
                    applyViewpointProperties(viewpoint, arcRotateCamera);
                    arcRotateCamera.alpha = orbitRotation.y - Math.PI / 2;
                    arcRotateCamera.beta = Math.PI / 2 - orbitRotation.x;
                    arcRotateCamera.radius = 0.0001;

                    return arcRotateCamera;
                } else {
                    const universalCamera = new UniversalCamera(viewpoint.id, viewpoint.position.toVec3(), this.scene);

                    applyViewpointProperties(viewpoint, universalCamera);

                    return universalCamera;
                }
        }

        return null;

        function applyViewpointProperties(viewpoint: ViewpointNode, camera: KbArcRotateCamera | UniversalCamera) {
            if (viewpoint.projection === eViewpointProjection.orthographic) {
                camera.mode = Camera.ORTHOGRAPHIC_CAMERA;
                camera.maxZ = 200000;
                if (camera instanceof ArcRotateCamera) {
                    camera.orthoWidth = viewpoint.fov || 8;
                    camera.wheelPrecision = 60;
                    camera.pinchPrecision = 60;
                    camera.setPosition(
                        viewpoint.position.toVec3().subtract(camera.target).normalize().scale(5000).add(camera.target)
                    );
                }
            } else if (viewpoint.projection === eViewpointProjection.perspective) {
                camera.mode = Camera.PERSPECTIVE_CAMERA;
                camera.fov = (viewpoint.fov * Math.PI) / 180;
                if (camera instanceof ArcRotateCamera) {
                    camera.wheelDeltaPercentage = 0.01;
                    //camera.wheelPrecision = 10;
                    camera.speed = 0.0001;
                    camera.lowerRadiusLimit = 0.0001;

                    camera.pinchDeltaPercentage = 0.001;
                    camera.setPosition(viewpoint.position.toVec3());
                }
            }

            if (camera instanceof ArcRotateCamera) {
                if (!viewpoint.allowOrbit) {
                    camera.lockRotation = true;
                }
                if (!viewpoint.allowPan) {
                    camera.lockPanning = true;
                }
                if (!viewpoint.allowZoom) {
                    camera.lockZoom = true;
                }
                camera.useFramingBehavior = false;
                camera.targetScreenOffset.x = viewpoint.offsetX;
                camera.targetScreenOffset.y = viewpoint.offsetY;
                if (viewpoint.limitMinimumTargetDistance) {
                    viewer.bbCache.rootBoundingBoxChanged
                        .pipe(takeUntil(dispose))
                        .pipe(startWith(null))
                        .subscribe(() => {
                            const bounding = viewer.bbCache.getWorld(sceneNode);
                            if (bounding) {
                                const minVector = new Vector3(bounding.min.x, bounding.min.y, bounding.min.z);
                                const maxVector = new Vector3(bounding.max.x, bounding.max.y, bounding.max.z);
                                const boundingSphere = new BoundingSphere(minVector, maxVector);
                                camera.lowerRadiusLimit = boundingSphere.radiusWorld;
                            } else {
                                camera.lowerRadiusLimit = viewpoint.minTargetDistance;
                            }
                        });
                } else {
                    camera.lowerRadiusLimit = viewpoint.minTargetDistance;
                }

                camera.upperRadiusLimit = viewpoint.maxTargetDistance;

                if (viewpoint.orbitAxis) {
                    const upVector = viewpoint.orbitAxis.toVec3();
                    if (upVector.length() === 0) {
                        upVector.set(0, 1, 0);
                    }
                    camera.upVector = upVector;
                }
                if (viewpoint.limitLngOrbit) {
                    camera.lowerAlphaLimit = (viewpoint.limitMinLongitude * Math.PI) / 180;
                    camera.upperAlphaLimit = (viewpoint.limitMaxLongitude * Math.PI) / 180;
                } else {
                    camera.lowerAlphaLimit = null;
                    camera.upperAlphaLimit = null;
                }

                if (viewpoint.limitLatOrbit) {
                    camera.lowerBetaLimit = (viewpoint.limitMinLatitude * Math.PI) / 180;
                    camera.upperBetaLimit = (viewpoint.limitMaxLatitude * Math.PI) / 180;
                } else {
                    camera.lowerBetaLimit = 0.001;
                    camera.upperBetaLimit = Math.PI - 0.001;
                }
            } else if (camera instanceof UniversalCamera) {
                // // For fixed rotation, only allow targeting the camera with the mouse and disable movement.
                // if (viewpoint.type === eViewpointType.fixedPosition) {
                //     camera.inputs.clear();
                //     camera.inputs.addMouse();
                // }
                camera.inputs.clear();
                if (viewpoint.allowPan) {
                    enableFreeMove(camera);
                }
                if (viewpoint.allowOrbit) {
                    enableFreeLook(camera);
                }
            }

            if (viewpoint.targetMode === eViewpointTargetMode.point) {
                camera.setTarget(viewpoint.targetPoint.toVec3());
            } else if (viewpoint.targetMode === eViewpointTargetMode.mesh) {
                const targetMeshNode = viewpoint._manager!.getById(viewpoint.targetMeshId!) as MeshNode;
                if (targetMeshNode) {
                    // TODO: When updating to babylon 5.0, use targetHost property for targetting mesh
                    bbCache.rootBoundingBoxChanged.pipe(takeUntil(dispose)).subscribe(() => {
                        const bbInfo = bbCache.getWorld(targetMeshNode);
                        if (bbInfo) {
                            camera.setTarget(bbInfo.center.toVec3());
                        }
                    });
                }
            } else {
                // rotation
                if (camera instanceof ArcRotateCamera) {
                    camera.alpha = (viewpoint.rotation.y * Math.PI) / 180; // yaw = longitude
                    camera.beta = (viewpoint.rotation.x * Math.PI) / 180; // pitch = latitude
                } else {
                    camera.rotation = viewpoint.rotation.toVec3().scale(Math.PI / 180);
                }
            }

            bbCache
                .watchForChanges(sceneNode)
                .pipe(takeUntil(dispose))
                .subscribe(bbInfo => {
                    if (bbInfo && bbInfo.boundingInfo.diagonalLength > 0) {
                        const cameraDistance = bbInfo.boundingInfo.diagonalLength * 2;
                        /**
                         * This piece of code is very important. if the maxZ is too small, things will disappear as you zoom out.
                         * If the maxZ is too large, the camera will not have enough buffer and
                         * all kinds of things get messed up - clicking doesn't work, you start getting z-fighting.
                         * Smallest the maxZ should be "10" due to the grid.
                         * However ortho camera should be double that because calculations are made from half of the frame
                         * And perspective camera should also take the camera distance into account (but have some sane multiplier based on the mesh size)
                         */
                        // should we set the minZ here as well, or just hard code it?
                        if (
                            viewpoint.projection === eViewpointProjection.orthographic &&
                            camera instanceof ArcRotateCamera
                        ) {
                            camera.setPosition(
                                camera.position
                                    .subtract(camera.target)
                                    .normalize()
                                    .scale(cameraDistance)
                                    .add(camera.target)
                            );
                            camera.maxZ = Math.max(cameraDistance, 10) * 2;
                        } else if (camera instanceof ArcRotateCamera && camera.upperRadiusLimit) {
                            camera.maxZ = Math.max(
                                Math.max(cameraDistance, 10) * 16,
                                camera.upperRadiusLimit + cameraDistance
                            );
                        } else {
                            camera.maxZ = Math.max(cameraDistance, 10) * 16;
                        }

                        // If maxZ is too high then it breaks clicking because the unprojected rays start returning NaN/Infinity
                        if (camera.maxZ > 200000) {
                            camera.maxZ = 200000;
                        }

                        if (viewer.rendererManager.sceneRenderer?.skybox) {
                            // need to resize skybox if it exists, otherwise it will get clipped
                            const scale = camera.maxZ / 2 / SKYBOX_SIZE;
                            viewer.rendererManager.sceneRenderer.skybox.scaling = new Vector3(scale, scale, scale);
                        }
                    }
                });

            camera.onDisposeObservable.add(() => {
                dispose.next();
                dispose.complete();
            });
            camera.minZ = 0.01; // hack the camera minz until we have a more automated way to set it
            camera.doNotSerialize = true;
        }
    }

    orient(orientation: eCameraOrientation) {
        const bbCache = this.kbViewer.bbCache;

        if (this._lastOrientation === eCameraOrientation.perspective) {
            this._lastPerspectivePosition = KbVector.From(this.scene.activeCamera!.position);
        }
        //get a viewpoint representing the current camera settings
        const viewpoint = new ViewpointNode();
        const camera = this.scene.activeCamera as KbArcRotateCamera | UniversalCamera;
        copyCameraStateIntoViewpoint(viewpoint, camera);

        let targetPoint = KbVector.Zero();
        if (viewpoint.targetMode == eViewpointTargetMode.point) {
            targetPoint = viewpoint.targetPoint;
        } else if (viewpoint.targetMode == eViewpointTargetMode.mesh) {
            const targetMeshNode = this.kbViewer.sceneNode._manager!.getById(viewpoint.targetMeshId!) as MeshNode;
            if (targetMeshNode) {
                const bbInfo = bbCache.getWorld(targetMeshNode);
                if (bbInfo) {
                    targetPoint = bbInfo.center;
                }
            }
        }
        let distance = viewpoint.position.subtract(targetPoint).length();

        let newPos = targetPoint.toObject();
        if (orientation == eCameraOrientation.top) {
            newPos.y += distance;
        } else if (orientation == eCameraOrientation.front) {
            newPos.z += distance;
        } else if (orientation == eCameraOrientation.left) {
            newPos.x -= distance;
        } else if (orientation == eCameraOrientation.back) {
            newPos.z -= distance;
        } else if (orientation == eCameraOrientation.right) {
            newPos.x += distance;
        } else if (orientation == eCameraOrientation.bottom) {
            newPos.y -= distance;
        } else if (orientation == eCameraOrientation.perspective) {
            const lastLength = this._lastPerspectivePosition.length();
            const ratio = distance / lastLength;
            newPos = this._lastPerspectivePosition.scale(ratio);
        }
        viewpoint.position = KbVector.From(newPos);
        if (orientation != eCameraOrientation.perspective) {
            viewpoint.projection = eViewpointProjection.orthographic;
            viewpoint.allowOrbit = false;
        } else {
            viewpoint.projection = eViewpointProjection.perspective;
            viewpoint.allowOrbit = true;
        }

        if (
            this._lastOrientation === eCameraOrientation.perspective &&
            orientation !== eCameraOrientation.perspective
        ) {
            //switching to orthographic so we need to deal with fov
            viewpoint.fov = 2 * distance * Math.tan(toRadians(viewpoint.fov / 2));
        } else if (
            this._lastOrientation !== eCameraOrientation.perspective &&
            orientation === eCameraOrientation.perspective
        ) {
            const orthoWidth = viewpoint.fov; //in orthographic, fov is the ortho width
            distance = orthoWidth / (2 * Math.tan(toRadians(defaults.ViewpointNode.fov / 2)));
            viewpoint.position = this._lastPerspectivePosition.scale(distance / this._lastPerspectivePosition.length());
            viewpoint.fov = defaults.ViewpointNode.fov;
        }
        viewpoint.offsetX = 0;
        viewpoint.offsetY = 0;

        this.moveCameraToViewpoint(viewpoint);
        this.frameCamera(this.kbViewer.sceneNode, {
            rotate: false,
            pan: true,
            zoom: false,
        });

        this._lastOrientation = orientation;
    }
}

function enableFreeLook(camera: UniversalCamera) {
    camera.inputs.addMouse();
}

function enableFreeMove(camera: UniversalCamera) {
    camera.inputs.addKeyboard();
    camera.inputs.addTouch();
}

function copyCameraStateIntoViewpoint(viewpoint: Partial<ViewpointNode>, camera: Camera) {
    let normalizedPosition = camera.position;

    // If this is an orthographic camera, the real position is set very far away to prevent clipping. So normalize the position so the biggest dimension is 5.
    if (camera instanceof ArcRotateCamera && camera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
        normalizedPosition = normalizedPosition.subtract(camera.position).normalize().add(camera.position);
        if (normalizedPosition.length() > 0) {
            const scale = Math.max(...normalizedPosition.asArray().map(dim => Math.abs(dim)));
            normalizedPosition = Vector3.FromArray(
                normalizedPosition.asArray().map(dim => Math.round((dim / scale) * 500) / 100)
            );
        }
    }

    if (camera instanceof FreeCamera) {
        const rotDegrees = camera.rotation.scale(180 / Math.PI);
        viewpoint.rotation = KbVector.FromVec3(rotDegrees);
    }
    viewpoint.position = KbVector.FromVec3(normalizedPosition);
    viewpoint.offsetX = camera instanceof KbArcRotateCamera ? camera.targetScreenOffset.x : 0;
    viewpoint.offsetY = camera instanceof KbArcRotateCamera ? camera.targetScreenOffset.y : 0;
    viewpoint.fov =
        camera.mode === Camera.ORTHOGRAPHIC_CAMERA
            ? camera.orthoRight! - camera.orthoLeft!
            : (camera.fov * 180) / Math.PI;
    viewpoint.projection =
        camera.mode === Camera.ORTHOGRAPHIC_CAMERA
            ? eViewpointProjection.orthographic
            : eViewpointProjection.perspective;
}

function normalizeRotation(alpha: number): number {
    const oneRotation = Math.PI * 2;
    const rotations = Math.floor(alpha / oneRotation);
    const radiansToRemove = rotations > 1 ? rotations * oneRotation : 0;
    const normalizedRotation = alpha - radiansToRemove;
    return normalizedRotation;
}
